import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, Loader2, X, Instagram, MessageCircle, Save, CalendarClock } from 'lucide-react';
import api from '../../services/api';
import { DisparoMassaModal } from '../Vendas/FunilCorretoresKanban';

/**
 * Imagens escolhidas no chat → legenda (IA) → agenda: Instagram (publicado pelo Postiz na hora marcada, após aprovação do gerente)
 * ou disparo em massa no WhatsApp (mesmo motor do funil de corretores: escolhe a instância, os contatos e o horário).
 */
const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200';
const btn = 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50';
const msgErro = (e) => e.response?.data?.error || e.message;
const usuarioAtual = () => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } };

async function urlParaStep(img, legenda) {
  const blob = await (await fetch(img.url)).blob();
  const nome = img.url.split('/').pop();
  const arquivo = new File([blob], nome, { type: blob.type || (img.tipo === 'video' ? 'video/mp4' : 'image/jpeg') });
  return { tipo: img.tipo === 'video' ? 'video' : 'imagem', conteudo: '', arquivo, preview: URL.createObjectURL(blob), nomeOriginal: nome, legenda };
}

export default function PublicarImagens({ imagens, empreendimentos = [], onClose }) {
  const [empreendimento, setEmpreendimento] = useState('');
  const [instrucao, setInstrucao] = useState('');
  const [temas, setTemas] = useState([]);
  const [tema, setTema] = useState('');
  const [temaUsado, setTemaUsado] = useState(null);
  const [titulo, setTitulo] = useState('');
  const [legenda, setLegenda] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [data, setData] = useState('');
  const [gerando, setGerando] = useState(false);
  const [salvando, setSalvando] = useState('');
  const [aviso, setAviso] = useState(null);   // { tipo: 'ok' | 'erro', texto }
  const [contas, setContas] = useState(null);   // contas de Instagram conectadas no Postiz
  const [contasSel, setContasSel] = useState([]);   // ids marcados (Instagram e/ou Facebook)
  const [zapAberto, setZapAberto] = useState(false);
  const [stepsZap, setStepsZap] = useState(null);
  const gerente = ['admin', 'gerente'].includes(usuarioAtual()?.perfil);

  const funil = useQuery({ queryKey: ['parceiros-funil-corretores'], queryFn: () => api.get('/parceiros/funil-corretores').then((r) => r.data.data), enabled: zapAberto });

  useEffect(() => {
    api.get('/marketing/imagens/temas').then((r) => setTemas(r.data.data)).catch(() => {});
    api.get('/marketing/imagens/contas-instagram').then((r) => { setContas(r.data.data); setContasSel((atual) => (atual.length ? atual : [r.data.data.find((c) => c.rede === 'instagram')?.id || r.data.data[0]?.id].filter(Boolean))); }).catch((e) => { setContas([]); setAviso({ tipo: 'erro', texto: `Contas conectadas: ${msgErro(e)}` }); });
  }, []);

  const gerar = async () => {
    setGerando(true); setAviso(null);
    try {
      const r = await api.post('/marketing/imagens/legenda', { imagens: imagens.filter((i) => i.tipo !== 'video').map((i) => i.id), empreendimento, instrucao, tema, legenda_atual: legenda }, { timeout: 180000 });
      const d = r.data.data; setTemaUsado(d.tema); setTitulo(d.titulo); setLegenda(d.legenda); setHashtags(d.hashtags);
    } catch (e) { setAviso({ tipo: 'erro', texto: msgErro(e) }); } finally { setGerando(false); }
  };

  const salvar = async (instagram) => {
    setAviso(null);
    if (instagram && !contasSel.length) return setAviso({ tipo: 'erro', texto: 'Marque ao menos uma conta para publicar.' });
    if (instagram && !data) return setAviso({ tipo: 'erro', texto: 'Escolha a data e hora da publicação.' });
    setSalvando(instagram ? 'ig' : 'rascunho');
    try {
      await api.post('/marketing/imagens/posts/de-imagens', { midias: imagens.map((i) => i.id), empreendimento, titulo, legenda, hashtags, agendado_para: data ? new Date(data).toISOString() : null, publicar_instagram: instagram, contas_ids: contasSel, contas_nome: (contas || []).filter((c) => contasSel.includes(c.id)).map((c) => `${c.rede === 'instagram' ? 'IG' : 'FB'} ${c.nome}`).join(', ') });
      setAviso({ tipo: 'ok', texto: instagram ? (gerente ? 'Agendado! O post será publicado nas contas marcadas na data marcada (acompanhe na aba Agenda de posts).' : 'Enviado! Aguardando um gerente aprovar na aba Agenda de posts; depois disso é publicado na data marcada.') : 'Salvo como rascunho na Agenda de posts.' });
    } catch (e) { setAviso({ tipo: 'erro', texto: msgErro(e) }); } finally { setSalvando(''); }
  };

  const abrirZap = async () => {
    setAviso(null); setSalvando('zap');
    try {
      const texto = legenda.trim();
      const steps = [];
      for (let i = 0; i < imagens.length; i++) steps.push(await urlParaStep(imagens[i], i === 0 ? texto : ''));
      setStepsZap(steps); setZapAberto(true);
    } catch (e) { setAviso({ tipo: 'erro', texto: `Não consegui carregar as imagens: ${e.message}` }); } finally { setSalvando(''); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Legenda e agendamento · {imagens.length} {imagens.length === 1 ? 'imagem' : 'imagens'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>
        <div className="flex gap-2 overflow-x-auto">{imagens.map((i) => (i.tipo === 'video' ? <video key={i.id} src={i.url} className="h-24 rounded-lg" /> : <img key={i.id} src={i.url} alt="" className="h-24 rounded-lg" />))}</div>

        <div className="grid md:grid-cols-3 gap-2">
          <select className={inp} value={tema} onChange={(e) => setTema(e.target.value)} title="Tema de campanha cadastrado na aba Temas">
            <option value="">Tema da campanha (opcional)</option>{temas.map((t) => <option key={t.id} value={t.nome}>{t.nome}</option>)}
          </select>
          <select className={inp} value={empreendimento} onChange={(e) => setEmpreendimento(e.target.value)}>
            <option value="">Empreendimento (opcional)</option>{empreendimentos.map((e) => <option key={e.id} value={e.nome}>{e.nome}</option>)}
          </select>
          <input className={inp} value={instrucao} onChange={(e) => setInstrucao(e.target.value)} placeholder="Instrução p/ a IA (ex.: tom mais direto, foco em lazer)" />
        </div>
        {temaUsado && <p className="text-xs text-blue-700">Tema aplicado na legenda: <b>{temaUsado}</b></p>}
        <button onClick={gerar} disabled={gerando} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>{gerando ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {legenda ? 'Gerar outra legenda' : 'Criar legenda com IA'}</button>

        <input className={inp} value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Título interno" />
        <textarea className={inp} rows={7} value={legenda} onChange={(e) => setLegenda(e.target.value)} placeholder="Legenda (gere com IA ou escreva)" />
        <input className={inp} value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="#hashtags" />

        {aviso && <p className={`text-sm rounded-lg px-3 py-2 ${aviso.tipo === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>{aviso.texto}</p>}

        <div className="border-t border-gray-100 pt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-700" title="Contas conectadas no Postiz">
              {contas === null && <span className="text-gray-400">Carregando contas…</span>}
              {contas?.length === 0 && <span className="text-gray-400">Nenhuma conta conectada no Postiz</span>}
              {contas?.map((c) => (
                <label key={c.id} className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={contasSel.includes(c.id)} onChange={() => setContasSel((p) => (p.includes(c.id) ? p.filter((x) => x !== c.id) : [...p, c.id]))} />
                  {c.rede === 'instagram' ? 'Instagram' : 'Facebook'} · {c.nome}
                </label>
              ))}
            </div>
            <CalendarClock size={16} className="text-gray-500" />
            <input type="datetime-local" className={`${inp} max-w-[230px]`} value={data} onChange={(e) => setData(e.target.value)} title="Data e hora da publicação" />
            <button onClick={() => salvar(true)} disabled={!!salvando || !legenda.trim() || !contasSel.length} className={`${btn} bg-pink-600 text-white hover:bg-pink-700`}>{salvando === 'ig' ? <Loader2 size={14} className="animate-spin" /> : <Instagram size={14} />} Agendar publicação</button>
          </div>
          {!gerente && <p className="text-xs text-gray-500">O agendamento no Instagram só é publicado depois que um gerente aprovar na Agenda de posts.</p>}
          <div className="flex flex-wrap gap-2">
            <button onClick={abrirZap} disabled={!!salvando} className={`${btn} bg-green-600 text-white hover:bg-green-700`}>{salvando === 'zap' ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />} Disparo em massa no WhatsApp…</button>
            <button onClick={() => salvar(false)} disabled={!!salvando || !legenda.trim()} className={`${btn} text-gray-600 hover:bg-gray-100`}><Save size={14} /> Salvar rascunho na agenda</button>
          </div>
        </div>
      </div>

      {zapAberto && funil.data && (
        <div onClick={(e) => e.stopPropagation()}>
          <DisparoMassaModal isOpen onClose={() => setZapAberto(false)} kanban={funil.data.grupos || {}} estagios={funil.data.estagios || []}
            initialSteps={stepsZap} origem="marketing_imagens" prefixoNome="Marketing imagens" titulo="Disparo no WhatsApp — imagem + legenda" />
        </div>
      )}
    </div>
  );
}
